<div class="w-full p-6 max-w-3xl">
  <form [formGroup]="courtForm" (ngSubmit)="onSubmit()">
    <!-- Section -->
    <div class="grid w-full grid-cols-4">
      <div class="text-xl col-span-1 mt-3">{{ isEditMode ? 'Edit Court' : 'New Court' }}</div>

      <!-- Sports Dropdown -->
      <mat-form-field class="col-span-1">
        <mat-select formControlName="sports" (selectionChange)="onSportChange($event.value)">

          <!-- Selected value template -->
          <mat-select-trigger>
            <img *ngIf="selectedSport" [src]="selectedSport.logo" [alt]="selectedSport.name"
              class="h-6 w-6 mr-2 inline-block rounded" />
            {{ selectedSport?.name }}
          </mat-select-trigger>

          <!-- Dropdown options -->
          <mat-option *ngFor="let sport of sportsList" [value]="sport.key">
            <img [src]="sport.logo" [alt]="sport.name" class="h-6 w-6 mr-2 inline-block rounded" />
            {{ sport.name }}
          </mat-option>
        </mat-select>
      </mat-form-field>
    </div>

    <div class="mb-10 mt-6 border-t"></div>

    <div class="mt-8 grid w-full gap-6 sm:grid-cols-4">
      <!-- Name -->
      <div class="sm:col-span-3">
        <mat-form-field class="w-full" subscriptSizing="dynamic">
          <mat-label>Court Name</mat-label>
          <input formControlName="name" matInput required (input)="onNameInputChange()">
          <mat-error *ngIf="courtForm.get('name')?.hasError('required')">
            Court Name is required
          </mat-error>
        </mat-form-field>
      </div>

      <!-- Status -->
      <div class="sm:col-span-1">
        <mat-form-field class="w-full" subscriptSizing="dynamic">
          <mat-label>Status</mat-label>
          <mat-icon class="icon-size-3"
            [style.color]="courtForm.get('status')?.value==='active' ? '#2E9B11' : '#F33779'" svgIcon="mat_solid:circle"
            matPrefix>
          </mat-icon>
          <mat-select formControlName="status">
            <mat-option value="active">Active</mat-option>
            <mat-option value="inactive">In-active</mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <!-- Court Type -->
      <div class="col-span-2">
        <div class="font-medium mb-2">Court Type</div>
        <div class="mt-3">
          <mat-button-toggle-group formControlName="courtType">
            <mat-button-toggle value="indoor">Indoor</mat-button-toggle>
            <mat-button-toggle value="outdoor">Outdoor</mat-button-toggle>
          </mat-button-toggle-group>
        </div>
        <mat-checkbox class="my-1" color="primary" formControlName="airConditioned">
          Air Conditioned
        </mat-checkbox>
      </div>

      <!-- Surface Type -->
      <div class="col-span-2">
        <div class="font-medium mb-2">Surface Type</div>
        <div class="mt-3">
          <mat-button-toggle-group formControlName="surfaceType">
            <mat-button-toggle value="grass">Grass</mat-button-toggle>
            <mat-button-toggle value="hard">Hard</mat-button-toggle>
          </mat-button-toggle-group>
        </div>
      </div>
      <!-- Min Capacity -->
      <div class="sm:col-span-2">
        <mat-form-field class="w-full" subscriptSizing="dynamic">
          <mat-label>Min Capacity</mat-label>
          <input type="number" formControlName="minCapacity" matInput>
          <mat-error *ngIf="courtForm.get('minCapacity')?.hasError('required')">
            Minimum Capacity is required
          </mat-error>
          <mat-error *ngIf="courtForm.get('minCapacity')?.hasError('min')">
            Minimum allowed is 1
          </mat-error>
          <mat-error *ngIf="courtForm.get('minCapacity')?.hasError('pattern')">
            Must be a whole number
          </mat-error>
        </mat-form-field>
      </div>
      <!-- Max Capacity -->
      <div class="sm:col-span-2">
        <mat-form-field class="w-full" subscriptSizing="dynamic">
          <mat-label>Max Capacity</mat-label>
          <input type="number" formControlName="maxCapacity" matInput>
          <mat-error *ngIf="courtForm.get('maxCapacity')?.hasError('required')">
            Maximum Capacity is required
          </mat-error>
          <mat-error *ngIf="courtForm.get('maxCapacity')?.hasError('min')">
            Minimum allowed is 1
          </mat-error>
          <mat-error *ngIf="courtForm.get('maxCapacity')?.hasError('max')">
            Maximum allowed is 100
          </mat-error>
          <mat-error *ngIf="courtForm.get('maxCapacity')?.hasError('pattern')">
            Must be a whole number
          </mat-error>
          <mat-error *ngIf="courtForm.get('maxCapacity')?.hasError('capacityRange')">
            Max Capacity must be equal or greater than Min Capacity
          </mat-error>
        </mat-form-field>
      </div>

      <!-- Price per hour -->
      <div class="sm:col-span-4">
        <mat-form-field class="w-full" subscriptSizing="dynamic">
          <mat-label>Default price per hour</mat-label>
          <input type="number" min="0" formControlName="pricePerHour" matInput>
          <span matPrefix class="mr-2">AED</span>
          <mat-hint>Used whenever no time-based interval applies.</mat-hint>
          <mat-error>Enter a non-negative default price.</mat-error>
        </mat-form-field>
      </div>

      <div class="sm:col-span-4">
        <div class="rounded-xl border border-gray-200 bg-white p-5">
          <mat-slide-toggle color="primary" formControlName="timeBasedPricing">
            Use time-based pricing
          </mat-slide-toggle>
          <p class="ml-12 mt-1 text-sm text-secondary">
            Charge different hourly prices by weekday and venue-local time.
          </p>
        </div>
      </div>

      <app-court-pricing-rules
        *ngIf="courtForm.get('timeBasedPricing')?.value"
        class="sm:col-span-4"
        [rules]="pricingRules">
      </app-court-pricing-rules>

      <!-- Pictures -->
      <div class="col-span-4">
        <div class="font-medium mb-1">Pictures</div>
        <div class="text-secondary font-small mb-5">
          Upload high quality images with min resolution 1920x1920
          and size under 5MB in supported format (PNG, JPG)
        </div>
        <div class="flex pt-1">
          <div class="relative flex h-40 px-1" *ngFor="let img of pictures.slice(0, 3); let i = index">
            <img [src]="img" alt="Card cover image" class="rounded object-cover">
            <div
              class="absolute inset-0 bg-black bg-opacity-40 opacity-0 hover:opacity-100 transition-opacity duration-200 rounded flex items-center justify-center">
              <button type="button" (click)="removePicture(i)"
                class="p-3 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-all">
                <mat-icon class="text-white text-3xl" svgIcon="heroicons_outline:trash"></mat-icon>
              </button>
            </div>
          </div>
          <div class="upload-box" (click)="openFilePicker()">
            <mat-icon class="icon-size-8 text-primary" svgIcon="heroicons_outline:plus"></mat-icon>
          </div>
          <input #fileInput type="file" multiple hidden (change)="onFileSelected($event)" accept="image/*" />
        </div>
      </div>
    </div>

    <div class="mb-5 mt-20 border-t"></div>

    <!-- Actions -->
    <div class="flex items-center justify-between">
      <button class="ml-4 pr-20 pl-20" mat-flat-button type="submit" color="primary"
        [disabled]="courtForm.invalid || isLoading">
        Save
      </button>
    </div>
  </form>
</div>
